import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Car,
  DollarSign,
  Calendar,
  Trophy,
  Star,
  Gem,
  TrendingUp,
} from 'lucide-react';
import { useGetHotWheels } from '../../hooks/use-hotwheels';
import { StatCard } from '../../component/stat-card/stat-card';
import { HotWheelCard } from '../../component/hotwheel-card/hotwheel-card';

/**
 * Statistics page — derived stats from the user's full car list.
 *
 * Stats:
 *   - Total Cars
 *   - Total Collection Value / Avg Value
 *   - Oldest Car (year)
 *   - Newest Car (year)
 *   - Most Expensive Car
 *   - Number of Treasure Hunts
 *   - Number of Super Treasure Hunts
 *   - Cars Added This Month
 *
 * Plus a "Most valuable" card row showing the top 3.
 */
export function HotWheelsStats() {
  const { t } = useTranslation();
  const { data, isLoading } = useGetHotWheels({ pageNo: 0, pageSize: 500 });
  const items = data?.items ?? [];

  const stats = useMemo(() => {
    if (items.length === 0) {
      return {
        total: 0,
        totalValue: 0,
        avgValue: 0,
        oldest: null as null | { year: number; car: (typeof items)[number] },
        newest: null as null | { year: number; car: (typeof items)[number] },
        mostExpensive: null as null | { value: number; car: (typeof items)[number] },
        ths: 0,
        sths: 0,
        addedThisMonth: 0,
        topThreeByValue: [] as typeof items,
      };
    }

    const totalValue = items.reduce(
      (s, c) => s + (c.EstimatedValue ?? 0),
      0
    );
    const avgValue = totalValue / items.length;

    const oldestYear = Math.min(...items.map((c) => c.ReleaseYear));
    const newestYear = Math.max(...items.map((c) => c.ReleaseYear));

    const oldestCar = items.find((c) => c.ReleaseYear === oldestYear);
    const newestCar = items.find((c) => c.ReleaseYear === newestYear);

    const valued = items.filter((c) => typeof c.EstimatedValue === 'number' && c.EstimatedValue > 0);
    const top =
      valued.length > 0
        ? valued.reduce((m, c) =>
            (c.EstimatedValue ?? 0) > (m?.EstimatedValue ?? 0) ? c : m
          )
        : null;

    const ths = items.filter((c) => c.TreasureHunt && !c.SuperTreasureHunt).length;
    const sths = items.filter((c) => c.SuperTreasureHunt).length;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const addedThisMonth = items.filter(
      (c) => new Date(c.CreatedDate ?? 0) >= monthStart
    ).length;

    const topThree = [...valued]
      .sort((a, b) => (b.EstimatedValue ?? 0) - (a.EstimatedValue ?? 0))
      .slice(0, 3);

    return {
      total: items.length,
      totalValue,
      avgValue,
      oldest: oldestCar ? { year: oldestYear, car: oldestCar } : null,
      newest: newestCar ? { year: newestYear, car: newestCar } : null,
      mostExpensive: top ? { value: top.EstimatedValue ?? 0, car: top } : null,
      ths,
      sths,
      addedThisMonth,
      topThreeByValue: topThree,
    };
  }, [items]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">{t('STATS')}</h1>
        <p className="text-zinc-400 text-sm mt-1">
          Insights from your collection.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <StatCard
          icon={Car}
          label={t('TOTAL_CARS')}
          value={isLoading ? '…' : stats.total}
          accent="red"
        />
        <StatCard
          icon={DollarSign}
          label={t('TOTAL_VALUE')}
          value={isLoading ? '…' : `$${stats.totalValue.toFixed(0)}`}
          accent="green"
        />
        <StatCard
          icon={TrendingUp}
          label={t('AVG_CAR_VALUE')}
          value={isLoading ? '…' : `$${stats.avgValue.toFixed(0)}`}
          accent="green"
        />
        <StatCard
          icon={Calendar}
          label={t('OLDEST_CAR')}
          value={isLoading || !stats.oldest ? '…' : stats.oldest.year}
          accent="blue"
        />
        <StatCard
          icon={Calendar}
          label={t('NEWEST_CAR')}
          value={isLoading || !stats.newest ? '…' : stats.newest.year}
          accent="blue"
        />
        <StatCard
          icon={Trophy}
          label={t('MOST_EXPENSIVE')}
          value={
            isLoading || !stats.mostExpensive
              ? '…'
              : `$${stats.mostExpensive.value.toFixed(0)}`
          }
          footer={
            stats.mostExpensive
              ? `${stats.mostExpensive.car.Brand} ${stats.mostExpensive.car.ModelNumber}`
              : ''
          }
          accent="gold"
        />
        <StatCard
          icon={Star}
          label={t('TREASURE_HUNTS')}
          value={isLoading ? '…' : stats.ths}
          accent="gold"
        />
        <StatCard
          icon={Gem}
          label={t('SUPER_TREASURE_HUNTS')}
          value={isLoading ? '…' : stats.sths}
          accent="red"
        />
        <StatCard
          icon={TrendingUp}
          label={t('ADDED_THIS_MONTH')}
          value={isLoading ? '…' : stats.addedThisMonth}
          accent="blue"
        />
      </div>

      {/* Most valuable */}
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4 text-yellow-500" />
          Most Valuable
        </h2>
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-2xl bg-zinc-900 animate-pulse"
              />
            ))}
          </div>
        ) : stats.topThreeByValue.length === 0 ? (
          <p className="text-zinc-500 text-sm">No valued cars yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.topThreeByValue.map((car) => (
              <HotWheelCard
                key={car.ItemId}
                car={car}
                onEdit={() => {}}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}